import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsApi } from '../services/api';

const SettingsValuesContext = createContext(null);

export function SettingsValuesProvider({ children }) {
  const [values, setValues] = useState({
    ticket_status: [],
    ticket_type: [],
    ticket_category: [],
    ticket_source: [],
    priority: [],
    asset_type: [],
    asset_category: [],
    asset_status: [],
    relationship_type: [],
    work_type: [],
    sla_tier: [],
    project_status: [],
    project_type: [],
    internal_project_type: [],
    billing_type: [],
    site_type: [],
    site_photo_category: [],
    internal_ticket_category: [],
    service_category: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchAllValues = useCallback(async () => {
    // Only fetch if we have an access token (user is authenticated)
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await settingsApi.list();
      const allSettings = response.data || [];
      
      // Group by category, filter active, sort by order
      const grouped = {};
      allSettings.forEach((setting) => {
        if (!grouped[setting.category]) {
          grouped[setting.category] = [];
        }
        if (setting.isActive !== false) {
          grouped[setting.category].push(setting);
        }
      });
      
      // Sort each category by order
      Object.keys(grouped).forEach(cat => {
        grouped[cat].sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      
      setValues(prev => ({ ...prev, ...grouped }));
    } catch (error) {
      console.error('Failed to fetch settings values:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllValues();
    
    // Listen for auth changes (login/logout) from same window
    const handleAuthChange = () => {
      fetchAllValues();
    };
    
    // Also listen for storage changes (cross-tab login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'accessToken') {
        fetchAllValues();
      }
    };
    
    window.addEventListener('auth-changed', handleAuthChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('auth-changed', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchAllValues]);

  const refresh = useCallback(() => {
    fetchAllValues();
  }, [fetchAllValues]);

  // Helper to get values for a specific category
  const getValues = useCallback((category) => {
    return values[category] || [];
  }, [values]);

  // Helper to get a single value by category and value
  const getValue = useCallback((category, value) => {
    const categoryValues = values[category] || [];
    return categoryValues.find(v => v.value === value);
  }, [values]);

  // Helper to get label for a value
  const getLabel = useCallback((category, value) => {
    const setting = getValue(category, value);
    return setting?.label || value;
  }, [getValue]);

  // Helper to get color for a value
  const getColor = useCallback((category, value) => {
    const setting = getValue(category, value);
    return setting?.color || null;
  }, [getValue]);

  // Helper to get ticket statuses by group ('active', 'completed', or null for all)
  const getTicketStatusesByGroup = useCallback((group) => {
    if (!group) return values.ticket_status;
    return values.ticket_status.filter(status => status.group === group);
  }, [values.ticket_status]);

  // Get status values for a specific group
  const getStatusValuesForGroup = useCallback((group) => {
    return values.ticket_status
      .filter(status => status.group === group)
      .map(status => status.value);
  }, [values.ticket_status]);

  // Get asset categories filtered by asset type
  // Returns categories that have the type in parentTypes, or have no parentTypes (all types)
  const getCategoriesForAssetType = useCallback((assetType) => {
    if (!assetType) return values.asset_category;
    return values.asset_category.filter(cat => {
      const parentTypes = cat.parentTypes;
      // If no parentTypes defined, show for all types (backward compatibility)
      if (!parentTypes || parentTypes.length === 0) return true;
      // Otherwise, check if the assetType is in the list
      return parentTypes.includes(assetType);
    });
  }, [values.asset_category]);

  // Site photo categories combine a managed "Site / General" list
  // (site_photo_category) with the existing Asset Categories. Resolve a label/
  // color for a photo's category from whichever list it belongs to.
  const getPhotoCategoryMeta = useCallback((value) => {
    if (!value) return null;
    const setting = (values.site_photo_category || []).find(v => v.value === value)
      || (values.asset_category || []).find(v => v.value === value);
    return setting ? { label: setting.label || value, color: setting.color || null } : { label: value, color: null };
  }, [values.site_photo_category, values.asset_category]);

  return (
    <SettingsValuesContext.Provider value={{
      values,
      loading,
      refresh,
      getValues,
      getValue,
      getLabel,
      getColor,
      getTicketStatusesByGroup,
      getStatusValuesForGroup,
      getCategoriesForAssetType,
      getPhotoCategoryMeta,
      // Direct access to common categories
      ticketStatuses: values.ticket_status,
      ticketTypes: values.ticket_type,
      ticketCategories: values.ticket_category,
      priorities: values.priority,
      assetTypes: values.asset_type,
      assetCategories: values.asset_category,
      assetStatuses: values.asset_status,
      relationshipTypes: values.relationship_type,
      workTypes: values.work_type,
      slaTiers: values.sla_tier,
      projectStatuses: values.project_status,
      projectTypes: values.project_type,
      internalProjectTypes: values.internal_project_type,
      billingTypes: values.billing_type,
      siteTypes: values.site_type,
      sitePhotoCategories: values.site_photo_category,
      ticketSources: values.ticket_source,
      internalTicketCategories: values.internal_ticket_category,
      serviceCategories: values.service_category,
    }}>
      {children}
    </SettingsValuesContext.Provider>
  );
}

export function useSettingsValues() {
  const context = useContext(SettingsValuesContext);
  if (!context) {
    throw new Error('useSettingsValues must be used within a SettingsValuesProvider');
  }
  return context;
}

// Fallback values for when settings haven't loaded yet
export const defaultValues = {
  ticket_status: [
    { value: 'new', label: 'New', color: '#3b82f6' },
    { value: 'assigned', label: 'Assigned', color: '#8b5cf6' },
    { value: 'in_progress', label: 'In Progress', color: '#f59e0b' },
    { value: 'waiting_customer', label: 'Waiting Customer', color: '#6366f1' },
    { value: 'waiting_vendor', label: 'Waiting Vendor', color: '#6366f1' },
    { value: 'resolved', label: 'Resolved', color: '#22c55e' },
    { value: 'closed', label: 'Closed', color: '#6b7280' },
  ],
  ticket_type: [
    { value: 'incident', label: 'Incident', color: '#ef4444' },
    { value: 'service_request', label: 'Service Request', color: '#3b82f6' },
    { value: 'problem', label: 'Problem', color: '#f97316' },
    { value: 'change', label: 'Change', color: '#8b5cf6' },
  ],
  priority: [
    { value: 'critical', label: 'Critical', color: '#ef4444' },
    { value: 'high', label: 'High', color: '#f97316' },
    { value: 'medium', label: 'Medium', color: '#eab308' },
    { value: 'low', label: 'Low', color: '#6b7280' },
  ],
  asset_type: [
    { value: 'hardware', label: 'Hardware', color: '#3b82f6' },
    { value: 'software', label: 'Software', color: '#8b5cf6' },
    { value: 'service', label: 'Service', color: '#22c55e' },
    { value: 'network', label: 'Network', color: '#06b6d4' },
    { value: 'virtual', label: 'Virtual', color: '#ec4899' },
    { value: 'cloud', label: 'Cloud', color: '#14b8a6' },
  ],
  asset_status: [
    { value: 'active', label: 'Active', color: '#22c55e' },
    { value: 'in_stock', label: 'In Stock', color: '#3b82f6' },
    { value: 'deployed', label: 'Deployed', color: '#22c55e' },
    { value: 'repair', label: 'In Repair', color: '#f59e0b' },
    { value: 'retired', label: 'Retired', color: '#6b7280' },
  ],
  work_type: [
    { value: 'remote_support', label: 'Remote Support', color: '#3b82f6' },
    { value: 'onsite_support', label: 'Onsite Support', color: '#8b5cf6' },
    { value: 'project_work', label: 'Project Work', color: '#22c55e' },
    { value: 'consultation', label: 'Consultation', color: '#f59e0b' },
    { value: 'installation', label: 'Installation', color: '#06b6d4' },
    { value: 'maintenance', label: 'Maintenance', color: '#ec4899' },
    { value: 'training', label: 'Training', color: '#14b8a6' },
    { value: 'travel', label: 'Travel', color: '#6b7280' },
    { value: 'other', label: 'Other', color: '#9ca3af' },
  ],
};
